import { userStore } from "/@/store/modules/user";
import { Role } from "/@/views/admin-roles/types";
import type { Router } from "vue-router";

/**
 * 不依赖 FB 专页的模块前缀 —— 「一个专页都没绑」时也必须放行。
 *
 * WhatsApp 模块整条链路都不碰 `userStore.getPageId`：账号上下文来自路由参数
 * `:phoneNumberId`，后端 `GET /whatsapp/accounts` 也只按 `visibleTo($user)` 过滤，
 * 与 `pages` 字段无关（docs/whatsapp.md、spec §4.0）。
 *
 * 而它的**绑定入口本身**就在 /whatsapp/index —— 一并拦掉就成了死循环：
 * 想绑 WhatsApp 得先绑一个 FB 专页。专页与 WhatsApp 是两条独立的接入渠道，
 * 只有 WhatsApp 的客户根本没有专页可绑。
 */
const PAGE_FREE_PREFIXES = ["/whatsapp"];

function isPageFreeRoute(path: string): boolean {
  return PAGE_FREE_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function createPagesGuard(router: Router) {
  const redirectPath = `/pages/unconnected`;
  router.beforeEach(async (to, from, next) => {
    //  如果当前用户pages
    if ([redirectPath, "/login", "/pages/manage", "/guest/home"].includes(to.path)) {
      next();
      return;
    }

    //  不吃专页上下文的模块（WhatsApp），任何角色、任何专页状态都放行
    if (isPageFreeRoute(to.path)) {
      next();
      return;
    }

    //  如果是组长或者超级管理员, 不进行限制
    let roles = userStore.getUserInfoState.roles;
    if (roles.includes(Role.SUPER_ADMIN) || roles.includes(Role.LEADER)) {
      next();
      return;
    }

    //  如果当前用户是畅聊人员, 只允许进入订阅者路由
    if (roles.includes(Role.AGENT) && ["subscribers", "home"].includes(to.name)) {
      next();
      return;
    }

    if (userStore.getUserInfoState.pages.length === 0) {
      if (to.path === redirectPath) {
        next();
      } else {
        next({
          path: redirectPath,
          replace: true,
        });
      }
    } else {
      next();
    }
  });
}
